import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useMotif } from "../contexts/MotifContext";
import { useEventSocket } from "../lib/ws";
import api, { formatErr, fileUrl } from "../lib/api";
import { toast } from "sonner";
import {
  Calendar, Utensils, Music, Camera, HelpCircle, MapPin, Bell, ConciergeBell,
  LogOut, Send, Heart, ThumbsUp, Trophy, MessageCircle, Repeat, MoreHorizontal,
  Scissors, Video, Languages
} from "lucide-react";
import Lightbox from "../components/Lightbox";
import CocktailOverlay from "../components/CocktailOverlay";
import MomentBanner from "../components/MomentBanner";
import PhotoQueueCard from "../components/PhotoQueueCard";
import EmbroideryFlow from "../components/EmbroideryFlow";
import LivestreamPlayer from "../components/LivestreamPlayer";
import SpeechCard from "../components/SpeechCard";
import NotificationSettings from "../components/NotificationSettings";
import CoupleCrossword from "../components/CoupleCrossword";
import CrosswordIntroModal from "../components/CrosswordIntroModal";
import WhoWillGuest from "../components/WhoWillGuest";
import MenuExplorer from "../components/MenuExplorer";
import CocktailGrid from "../components/CocktailGrid";
import PhotoPicker from "../components/PhotoPicker";
import MusicSearch from "../components/MusicSearch";
import VenueMapModal from "../components/VenueMapModal";
import { t, T, getLang, setLang, LANGS } from "../lib/i18n";
import { to12Hour, nextEventIndex, formatTimestamp } from "../lib/time";

const TAB_DEFS = [
  { id: "home", key: "home", icon: Heart },
  { id: "feed", key: "feed", icon: Camera },
  { id: "request", key: "requests", icon: ConciergeBell, ariaLabel: "Requests" },
  { id: "games", key: "games", icon: Trophy },
  { id: "more", key: "more", icon: MoreHorizontal },
];

export default function GuestApp() {
  const { user, logout } = useAuth();
  const { motif, eventId } = useMotif();
  const navigate = useNavigate();
  const guest = user.guest;

  // Language Memory: on load, apply the language saved on the guest profile
  // (set from any device). Profile preference wins over this device's default.
  useEffect(() => {
    const pref = guest?.language;
    if (pref && pref !== getLang()) setLang(pref);
    // run once per guest session
  }, [guest?.id]);
  const [tab, setTab] = useState("home");
  const [requestsSub, setRequestsSub] = useState("food");
  const [moreSub, setMoreSub] = useState(null);

  // Phase 5D: deep-link from push notifications, e.g. /guest?tab=request&sub=music
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const t = p.get("tab");
    const s = p.get("sub");
    if (!t) return;
    const tabId = t === "requests" ? "request" : t;
    setTab(tabId);
    if (s) {
      if (tabId === "request") setRequestsSub(s);
      else if (tabId === "more") setMoreSub(s);
    }
    window.history.replaceState({}, "", window.location.pathname);
  }, []);
  const openRequests = (sub) => { setRequestsSub(sub); setTab("request"); };

  // Whenever we switch tabs — and especially onto Requests — scroll to the very top.
  // Guests must never land halfway through the Food page.
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
    }
  }, [tab, requestsSub]);
  const [event, setEvent] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [myReqs, setMyReqs] = useState([]);
  const [music, setMusic] = useState([]);
  const [feed, setFeed] = useState([]);
  const [menu, setMenu] = useState([]);
  const [cocktails, setCocktails] = useState([]);
  const [activeCocktail, setActiveCocktail] = useState(null);
  const [activeMoment, setActiveMoment] = useState(null);
  const [scheduleFocus, setScheduleFocus] = useState(0); // increments when UP NEXT tapped → schedule scrolls to next event
  const [, forceRerender] = useState(0);

  useEffect(() => {
    const handler = () => forceRerender((n) => n + 1);
    window.addEventListener("venuefy-lang-change", handler);
    return () => window.removeEventListener("venuefy-lang-change", handler);
  }, []);

  const refresh = useCallback(async () => {
    const [ev, sch, ann, mine, mus, fd, mn, ck] = await Promise.allSettled([
      api.get("/events/current"),
      api.get("/schedule"),
      api.get("/announcements"),
      api.get("/requests/food-drink"),
      api.get("/music"),
      api.get("/feed"),
      api.get("/menu"),
      api.get("/cocktails"),
    ]);
    if (ev.status === "fulfilled") setEvent(ev.value.data);
    if (sch.status === "fulfilled") setSchedule(sch.value.data);
    if (ann.status === "fulfilled") setAnnouncements(ann.value.data);
    // Phase 3B: filter out any legacy drink rows from client — venue handles drinks independently
    if (mine.status === "fulfilled") setMyReqs(mine.value.data.filter((r) => r.category !== "drink"));
    if (mus.status === "fulfilled") setMusic(mus.value.data);
    if (fd.status === "fulfilled") setFeed(fd.value.data);
    if (mn.status === "fulfilled") setMenu(mn.value.data);
    if (ck.status === "fulfilled") setCocktails(ck.value.data);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEventSocket(eventId, (msg) => {
    const t = msg.type;
    if (t === "fd_request_update" && msg.payload.guest_id === guest.id && msg.payload.category !== "drink") {
      setMyReqs((r) => r.map((x) => (x.id === msg.payload.id ? msg.payload : x)));
      if (msg.payload.status === "delivered") toast.success("Your order is on its way!");
    }
    if (t === "announcement") setAnnouncements((a) => [msg.payload, ...a]);
    if (t === "music_new" || t === "music_update") {
      api.get("/music").then(({ data }) => setMusic(data));
    }
    if (t === "feed_new" || t === "feed_update") {
      api.get("/feed").then(({ data }) => setFeed(data));
    }
    if (t === "cocktail_announce") {
      setActiveCocktail(msg.payload.cocktail);
    }
    if (t === "moment") {
      setActiveMoment(msg.payload);
    }
    if (t === "experience_request_update" && msg.payload.guest_id === guest.id) {
      const s = (msg.payload.status || "").toUpperCase();
      if (s === "COMPLETED" || s === "READY") {
        toast.success("Your favor is ready for pickup at the Embroidery Pickup Table.");
      }
    }
  });

  const lastFood = useMemo(
    () => myReqs.filter((r) => r.category === "food").sort((a, b) => b.created_at.localeCompare(a.created_at))[0] || null,
    [myReqs]
  );

  const reorder = async (lastReq) => {
    if (!lastReq || lastReq.category === "drink") return;
    try {
      await api.post("/requests/food-drink", {
        category: lastReq.category,
        item: lastReq.item,
        notes: lastReq.notes || "",
      });
      toast.success(`Reordered: ${lastReq.item}`);
      const { data } = await api.get("/requests/food-drink");
      setMyReqs(data.filter((r) => r.category !== "drink"));
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="min-h-screen bg-motif fadein" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 4rem)" }}>
      {/* Header */}
      <header className="px-5 pt-3 pb-3 flex items-start justify-between">
        <div>
          <div className="text-[10px] tracking-luxe text-motif-muted">{T("welcome")}</div>
          <h1 className="font-display text-3xl text-motif leading-tight">{guest.first_name}</h1>
          <div className="text-xs text-motif-muted mt-0.5">
            <span className="text-primary-motif font-medium" data-testid="guest-table-header">
              {(guest.table_name || "").replace(/^Table\s+/i, "") ? `${T("Table")} ${(guest.table_name || "").replace(/^Table\s+/i, "")}` : (guest.table_name || "")}
            </span>
            {guest.seat ? ` · ${T("Seat")} ${guest.seat}` : ""}
          </div>
        </div>
        <button onClick={() => { logout(); navigate("/riyazandaimee"); }} className="btn-ghost text-xs flex items-center gap-1.5" data-testid="guest-logout-btn">
          <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
          {T("Leave")}
        </button>
      </header>

      {/* Announcement banner */}
      {announcements[0] && (
        <div className="mx-5 mb-3 card-elev p-3 flex items-start gap-3 border-l-4" style={{ borderLeftColor: "var(--motif-primary)" }} data-testid="announcement-banner">
          <Bell className="w-4 h-4 text-primary-motif flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <div className="text-sm text-motif">{announcements[0].text}</div>
        </div>
      )}

      {/* Content wrapper — centered mobile column, bottom padding for fixed nav */}
      <div
        className="px-5 max-w-xl mx-auto"
        style={{ paddingBottom: "calc(96px + env(safe-area-inset-bottom, 0px))" }}
      >
        {tab === "home" && <HomeTab event={event} schedule={schedule} announcements={announcements} setTab={setTab} openRequests={openRequests} lastFood={lastFood} reorder={reorder} cocktails={cocktails} onUpNext={() => { setScheduleFocus(Date.now()); setTab("more"); }} />}
        {tab === "request" && <RequestsHub myReqs={myReqs} refresh={refresh} menu={menu} guest={guest} music={music} refreshMusic={() => api.get("/music").then(({data})=>setMusic(data))} initialSub={requestsSub} />}
        {tab === "feed" && <FeedTab feed={feed} refresh={() => api.get("/feed").then(({data})=>setFeed(data))} />}
        {tab === "games" && <GamesTab />}
        {tab === "more" && <MoreHub setTab={setTab} schedule={schedule} guest={guest} event={event} focusSignal={scheduleFocus} onFocusHandled={() => setScheduleFocus(0)} initialSub={moreSub} />}
      </div>

      <CocktailOverlay cocktail={activeCocktail} onClose={() => setActiveCocktail(null)} />
      <MomentBanner moment={activeMoment} onDismiss={() => setActiveMoment(null)} />

      {/* Bottom nav — portaled to <body> so no animated/transformed ancestor can
          ever become its containing block (iOS position:fixed reliability). */}
      {createPortal(
        <nav
          className="fixed bottom-0 left-0 right-0 z-30 glass border-t border-motif"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
          data-testid="guest-bottom-nav"
        >
          <div className="max-w-md mx-auto px-2 py-1 flex overflow-x-auto no-scrollbar gap-1">
            {TAB_DEFS.map((td) => {
              const Icon = td.icon;
              const active = td.id === tab;
              return (
                <button
                  key={td.id}
                  onClick={() => setTab(td.id)}
                  aria-label={td.ariaLabel || t(td.key)}
                  className="flex-1 min-w-[60px] flex flex-col items-center gap-0.5 py-1.5 rounded-xl transition"
                  style={{
                    background: active ? "var(--motif-accent)" : "transparent",
                    color: active ? "var(--motif-primary)" : "var(--motif-text-muted)",
                  }}
                  data-testid={`tab-${td.id}`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                  <span className="text-[10px] tracking-wide whitespace-nowrap">{t(td.key)}</span>
                </button>
              );
            })}
          </div>
        </nav>,
        document.body
      )}
    </div>
  );
}

function HomeTab({ event, schedule, announcements, setTab, openRequests, lastFood, reorder, cocktails, onUpNext }) {
  const [alertsOpen, setAlertsOpen] = useState(false);
  // Re-evaluate "next" every 30s so the card advances as the evening progresses
  const [, setMinuteTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setMinuteTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);
  const nextIdx = nextEventIndex(schedule, event?.timezone);
  const next = nextIdx >= 0 ? schedule[nextIdx] : null;
  const [mapOpen, setMapOpen] = useState(false);
  return (
    <div className="space-y-4 fadein">
      <div className="card-elev p-5">
        <div className="text-[10px] tracking-luxe text-motif-muted">{T("tonight")}</div>
        <div className="font-display text-2xl text-motif">{event?.bride} & {event?.groom}</div>
        <div className="text-sm text-motif-muted">{event?.venue}</div>
      </div>

      {lastFood && (
        <div className="card-elev p-4">
          <div className="flex items-center gap-2 mb-2">
            <Repeat className="w-3.5 h-3.5 text-primary-motif" strokeWidth={1.5} />
            <div className="text-[10px] tracking-luxe text-motif-muted">{T("quick reorder")}</div>
          </div>
          <button
            onClick={() => reorder(lastFood)}
            className="w-full flex items-center justify-between p-3 rounded-xl bg-accent-motif hover:opacity-90 transition"
            data-testid="reorder-food-btn"
          >
            <div className="text-left">
              <div className="text-[10px] tracking-luxe text-motif-muted">{T("your last bite")}</div>
              <div className="font-display text-lg text-motif">{lastFood.item}</div>
            </div>
            <span className="btn-primary text-xs px-4 py-2 flex items-center gap-1.5">
              <Repeat className="w-3 h-3" /> {T("Again")}
            </span>
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <QuickAction label={T("Food")} testKey="food" icon={Utensils} onClick={() => openRequests("food")} />
        <QuickAction label={T("Music")} testKey="music" icon={Music} onClick={() => openRequests("music")} />
        <QuickAction label={T("Feed")} testKey="feed" icon={Camera} onClick={() => setTab("feed")} />
        <QuickAction label={T("Map")} testKey="map" icon={MapPin} onClick={() => setMapOpen(true)} />
      </div>
      {mapOpen && <VenueMapModal onClose={() => setMapOpen(false)} />}
      <button
        onClick={() => openRequests("help")}
        className="card-elev p-4 w-full flex flex-col items-center justify-center gap-1.5 text-center hover:bg-accent-motif transition"
        data-testid="quick-help"
      >
        <div className="w-9 h-9 rounded-full bg-accent-motif flex items-center justify-center">
          <HelpCircle className="w-4 h-4 text-primary-motif" strokeWidth={1.5} />
        </div>
        <div className="font-display text-lg text-motif leading-tight">{T("Help")}</div>
        <div className="text-[10px] text-motif-muted">{T("Request assistance")}</div>
      </button>

      {/* Signature cocktails — informational only (venue handles drinks). Not orderable. */}
      <CocktailGrid cocktails={cocktails} />

      {next && (
        <button
          type="button"
          onClick={onUpNext}
          className="card-elev p-4 w-full text-left hover:bg-accent-motif transition"
          data-testid="up-next-card"
        >
          <div className="text-[10px] tracking-luxe text-motif-muted mb-1">{T("up next")}</div>
          <div className="flex items-baseline gap-3">
            <div className="font-display text-3xl text-primary-motif">{to12Hour(next.time)}</div>
            <div className="font-display text-lg text-motif">{next.title}</div>
          </div>
          {next.description && <div className="text-xs text-motif-muted mt-1">{next.description}</div>}
        </button>
      )}

      {announcements.length > 1 && (
        <div className="card-elev p-4">
          <div className="text-[10px] tracking-luxe text-motif-muted mb-2">{T("announcements")}</div>
          <div className="space-y-2">
            {announcements.slice(1, 4).map((a) => (
              <div key={a.id} className="text-sm text-motif border-l-2 pl-3" style={{ borderColor: "var(--motif-border)" }}>
                {a.text}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notifications (moved from More tab) — tap to open alert settings */}
      <button
        onClick={() => setAlertsOpen((o) => !o)}
        className="card-elev p-4 w-full min-h-[72px] flex flex-col items-center justify-center gap-1.5 transition"
        style={{
          borderColor: alertsOpen ? "var(--motif-primary)" : "var(--motif-border)",
          background: alertsOpen ? "var(--motif-accent)" : "var(--motif-surface)",
        }}
        data-testid="home-notifications-tile"
      >
        <Bell className="w-4 h-4 text-primary-motif flex-shrink-0" strokeWidth={1.5} />
        <span className="text-[11px] text-motif text-center">{T("Notifications")}</span>
      </button>
      {alertsOpen && <NotificationSettings />}
    </div>
  );
}

function QuickAction({ label, testKey, icon: Icon, onClick }) {
  return (
    <button onClick={onClick} className="card-elev p-4 flex flex-col items-center justify-center text-center gap-2 hover:bg-accent-motif transition" data-testid={`quick-${testKey || label.toLowerCase()}`}>
      <div className="w-11 h-11 rounded-full bg-accent-motif flex items-center justify-center">
        <Icon className="w-5 h-5 text-primary-motif" strokeWidth={1.5} />
      </div>
      <div className="font-display text-xl text-motif">{label}</div>
    </button>
  );
}

function ScheduleTab({ schedule, event, focusSignal, onFocusHandled }) {
  const itemRefs = useRef({});
  const [highlightId, setHighlightId] = useState(null);

  // When opened via the UP NEXT card, scroll to the next upcoming event
  // (event-local time) and highlight it for 3 seconds.
  //
  // Mobile-safe handoff (Phase 5C.1B):
  //  - waits via requestAnimationFrame until the target ref exists and has
  //    layout (bounded retry, max 8 frames — no polling loop)
  //  - scrolls the WINDOW (the real scroll container) with manual math that
  //    centers the item in the visible area ABOVE the fixed bottom nav, so
  //    iOS Safari's flaky scrollIntoView({block:"center"}) is not relied on
  //  - clears the pending focus signal after a successful focus so revisiting
  //    the More tab never re-scrolls
  useEffect(() => {
    if (!focusSignal || !schedule.length) return;
    const idx = nextEventIndex(schedule, event?.timezone);
    const target = idx >= 0 ? schedule[idx] : null;
    if (!target) return;
    let cancelled = false;
    let attempts = 0;
    const tryFocus = () => {
      if (cancelled) return;
      const el = itemRefs.current[target.id];
      if (!el || el.offsetHeight === 0) {
        // Element not mounted / laid out yet — bounded retry on next frame.
        if (attempts++ < 8) requestAnimationFrame(tryFocus);
        return;
      }
      const nav = document.querySelector('[data-testid="guest-bottom-nav"]');
      const navH = nav ? nav.getBoundingClientRect().height : 0;
      const visibleH = window.innerHeight - navH;
      // Absolute document Y from the layout tree (offsetTop chain). Unlike
      // scrollY + getBoundingClientRect().top, this is immune to races with
      // the parent scroll-to-top or an in-flight smooth scroll on mobile.
      let absY = 0;
      for (let n = el; n; n = n.offsetParent) absY += n.offsetTop;
      const top = Math.max(0, absY - Math.max(16, (visibleH - el.offsetHeight) / 2));
      try {
        window.scrollTo({ top, behavior: "smooth" });
      } catch {
        window.scrollTo(0, top); // very old engines: options object unsupported
      }
      setHighlightId(target.id);
      if (onFocusHandled) onFocusHandled(); // clear pending signal in parent
    };
    // Two frames: lets the parent tab-change scroll-to-top effect and the
    // first mobile layout pass complete before we position.
    const raf = requestAnimationFrame(() => requestAnimationFrame(tryFocus));
    return () => { cancelled = true; cancelAnimationFrame(raf); };
  }, [focusSignal, schedule.length]);

  // Highlight lifecycle kept separate so clearing the focus signal above can
  // never cancel the 3-second timer (no duplicate/orphaned timers).
  useEffect(() => {
    if (!highlightId) return;
    const t = setTimeout(() => setHighlightId(null), 3000);
    return () => clearTimeout(t);
  }, [highlightId]);

  const highlightStyle = (id) =>
    highlightId === id
      ? { boxShadow: "0 0 0 2px var(--motif-primary)", background: "var(--motif-accent)", transition: "box-shadow 0.4s ease, background 0.4s ease" }
      : { transition: "box-shadow 0.4s ease, background 0.4s ease" };

  return (
    <div className="space-y-2 fadein">
      <h2 className="font-display text-2xl text-motif mb-2">{T("Tonight's schedule")}</h2>
      {schedule.map((s) => (
        <div
          key={s.id}
          ref={(el) => { itemRefs.current[s.id] = el; }}
          className="rounded-2xl"
          style={{ scrollMarginTop: 16, scrollMarginBottom: 120, ...highlightStyle(s.id) }}
          data-testid={`schedule-item-${s.id}`}
        >
          {s.profile_attached ? (
            <SpeechCard item={s} />
          ) : (
            <div className="card-elev p-4 flex gap-4">
              <div className="font-display text-2xl text-primary-motif w-24 flex-shrink-0">{to12Hour(s.time)}</div>
              <div>
                <div className="font-display text-lg text-motif">{s.title}</div>
                <div className="text-xs text-motif-muted">{s.description}</div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function TableTab({ guest }) {
  const [tableInfo, setTableInfo] = useState(undefined); // undefined=loading, null=unassigned
  const [mapOpen, setMapOpen] = useState(false);

  useEffect(() => {
    api.get("/venue-map/my-table")
      .then(({ data }) => setTableInfo(data.assigned ? data : null))
      .catch(() => setTableInfo(null));
  }, []);

  const mapBehavior = useMemo(() => {
    if (!tableInfo) return null;
    const digits = String(tableInfo.table_name || "").replace(/\D/g, "");
    return {
      type: "MY_TABLE",
      tableNumber: digits ? Number(digits) : null,
      pinLabel: `Your Table: ${tableInfo.table_name}`,
      subtitle: tableInfo.seat ? `Seat ${tableInfo.seat}` : "",
    };
  }, [tableInfo]);

  return (
    <div className="space-y-3 fadein">
      <h2 className="font-display text-2xl text-motif mb-2">{T("My table")}</h2>
      <div className="card-elev p-6 text-center">
        <div className="text-[10px] tracking-luxe text-motif-muted">{T("you are at")}</div>
        <div className="font-display text-5xl text-primary-motif mt-2">{guest.table_name}</div>
        {guest.seat && <div className="text-sm text-motif-muted mt-2">{T("Seat")} {guest.seat}</div>}
        {tableInfo === null ? (
          <div className="text-sm text-motif-muted mt-4 italic" data-testid="my-table-unassigned">
            {T("Your table assignment is not currently available.")}
          </div>
        ) : (
          <button
            onClick={() => tableInfo && setMapOpen(true)}
            disabled={!tableInfo}
            className="btn-primary mt-4 w-full flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="my-table-view-map"
          >
            <MapPin className="w-4 h-4 flex-shrink-0" /> {T("View My Table on Map")}
          </button>
        )}
      </div>
      {mapOpen && tableInfo && (
        <VenueMapModal
          onClose={() => setMapOpen(false)}
          behavior={mapBehavior}
        />
      )}
    </div>
  );
}

const RESTRICTION_BADGE = {
  vegetarian: { label: "V", color: "#16a34a" },
  vegan: { label: "VG", color: "#15803d" },
  "gluten-free": { label: "GF", color: "#a16207" },
  "dairy-free": { label: "DF", color: "#0369a1" },
  pescatarian: { label: "Fish", color: "#0891b2" },
  "contains-dairy": { label: "Dairy", color: "#92400e" },
  "contains-nuts": { label: "Nuts", color: "#b91c1c" },
  spicy: { label: "Spicy", color: "#dc2626" },
};

function FoodTab({ myReqs, refresh, menu }) {
  const kitchenReqs = myReqs.filter((r) => r.category !== "drink");
  return (
    <div className="space-y-4 fadein">
      <h2 className="font-display text-2xl text-motif">{T("Food & menu")}</h2>

      {/* 1. Kitchen Requests — always first */}
      <section data-testid="food-kitchen-requests">
        <div className="text-[10px] tracking-luxe text-motif-muted mb-2">{T("Kitchen Requests")}</div>
        <OtherMode refresh={refresh} />
        {kitchenReqs.length > 0 && (
          <div className="space-y-2 mt-3">
            <div className="text-[10px] tracking-luxe text-motif-muted">{T("your requests")}</div>
            {kitchenReqs.slice(0, 8).map((r) => (
              <div key={r.id} className="card-elev p-3 flex items-center justify-between" data-testid={`my-req-${r.id}`}>
                <div>
                  <div className="font-display text-base text-motif">{r.item}</div>
                  <div className="text-[10px] text-motif-muted">
                    {formatTimestamp(r.created_at)} · {r.category}
                  </div>
                </div>
                <StatusPill s={r.status} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* 2. Menu Explorer */}
      <section data-testid="food-menu-explorer">
        <MenuExplorer menu={menu} />
      </section>
    </div>
  );
}

function MenuMode({ menu }) {
  const [filter, setFilter] = useState("all");
  const filters = [
    { id: "all", label: "All" },
    { id: "vegetarian", label: "V" },
    { id: "vegan", label: "VG" },
    { id: "gluten-free", label: "GF" },
    { id: "pescatarian", label: "Fish" },
  ];
  const filtered = useMemo(() => {
    if (filter === "all") return menu;
    return menu.filter((m) => (m.restrictions || []).includes(filter));
  }, [menu, filter]);

  if (menu.length === 0) {
    return <div className="text-motif-muted text-sm py-4 text-center">{T("Menu is being prepared…")}</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {filters.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className="px-3 py-1.5 rounded-full text-xs tracking-wide flex-shrink-0"
            style={{
              background: filter === f.id ? "var(--motif-primary)" : "var(--motif-accent)",
              color: filter === f.id ? "var(--motif-primary-fg)" : "var(--motif-text-muted)",
            }}
            data-testid={`menu-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {filtered.map((m) => (
          <MenuCard key={m.id} item={m} />
        ))}
      </div>
    </div>
  );
}

function MenuCard({ item }) {
  return (
    <div className="card-elev p-4 fadein" data-testid={`menu-card-${item.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="text-[10px] tracking-luxe text-motif-muted">{item.section?.replace("_", " ")}</div>
          <div className="font-display text-xl text-motif leading-tight">{item.name}</div>
        </div>
        {(item.restrictions || []).length > 0 && (
          <div className="flex flex-wrap gap-1 justify-end max-w-[40%]">
            {item.restrictions.map((r) => {
              const b = RESTRICTION_BADGE[r];
              if (!b) return null;
              return (
                <span
                  key={r}
                  className="text-[9px] tracking-wide px-1.5 py-0.5 rounded-full font-medium"
                  style={{ background: `${b.color}18`, color: b.color, border: `1px solid ${b.color}40` }}
                >
                  {b.label}
                </span>
              );
            })}
          </div>
        )}
      </div>
      {item.description && <p className="text-sm text-motif mt-2">{item.description}</p>}
      {item.location && (
        <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-motif">
          <MapPin className="w-3.5 h-3.5 text-primary-motif flex-shrink-0" strokeWidth={1.5} />
          <span className="text-xs text-motif">{item.location}</span>
        </div>
      )}
    </div>
  );
}

function OtherMode({ refresh }) {
  const [item, setItem] = useState("");
  const [notes, setNotes] = useState("");
  const send = async () => {
    if (!item.trim()) return toast.error(T("What do you need?"));
    try {
      await api.post("/requests/food-drink", { category: "food", item: item.trim(), notes });
      toast.success(T("Sent to the kitchen"));
      setItem(""); setNotes("");
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="card-elev p-4 space-y-2">
      <div className="text-[10px] tracking-luxe text-motif-muted">{T("custom food request")}</div>
      <input
        className="input-motif"
        placeholder={T("e.g. Extra naan, gluten-free portion, kid's plate")}
        value={item}
        onChange={(e) => setItem(e.target.value)}
        data-testid="food-custom-item"
      />
      <input
        className="input-motif"
        placeholder={T("Notes (optional)")}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        data-testid="food-custom-notes"
      />
      <button onClick={send} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="food-custom-send">
        <Send className="w-4 h-4" /> {T("Send to kitchen")}
      </button>
    </div>
  );
}

function StatusPill({ s }) {
  const colors = {
    new: "var(--motif-text-muted)",
    accepted: "#3b82f6",
    in_progress: "#f59e0b",
    delivered: "#10b981",
    cancelled: "#ef4444",
  };
  return (
    <span className="text-[10px] tracking-luxe px-2 py-1 rounded-full" style={{ background: "var(--motif-accent)", color: colors[s] || "var(--motif-text)" }}>
      {T((s || "").replace("_", " "))}
    </span>
  );
}

function MusicTab({ music, guestId, refresh }) {
  const vote = async (id) => {
    try { await api.post(`/music/${id}/vote`); refresh(); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="space-y-3 fadein">
      <h2 className="font-display text-2xl text-motif">Music requests</h2>
      <div className="card-elev p-4">
        <MusicSearch onSubmitted={refresh} />
      </div>
      <div className="space-y-2">
        {music.length === 0 && <div className="text-center text-motif-muted text-sm py-8">{T("No requests yet — be the first.")}</div>}
        {music.map((m) => {
          const voted = (m.votes || []).includes(guestId);
          return (
            <div key={m.id} className="card-elev p-3 flex items-center justify-between" data-testid={`music-row-${m.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                {m.artwork_url && <img src={m.artwork_url} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" loading="lazy" />}
                <div className="min-w-0">
                  <div className="font-display text-base text-motif truncate">{m.title}</div>
                  <div className="text-[11px] text-motif-muted truncate">{m.artist || "—"} · {T(m.status)}</div>
                </div>
              </div>
              <button
                onClick={() => vote(m.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition flex-shrink-0"
                style={{
                  background: voted ? "var(--motif-primary)" : "var(--motif-accent)",
                  color: voted ? "var(--motif-primary-fg)" : "var(--motif-text)",
                }}
                data-testid={`music-vote-${m.id}`}
              >
                <ThumbsUp className="w-3.5 h-3.5" strokeWidth={1.5} /> {m.vote_count}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FeedTab({ feed, refresh }) {
  const [photo, setPhoto] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [lbIndex, setLbIndex] = useState(-1);

  const photoPosts = useMemo(() => feed.filter((p) => p.image_url), [feed]);
  const lbItems = useMemo(
    () => photoPosts.map((p) => ({ src: fileUrl(p.image_url), caption: p.caption, author: p.author_name })),
    [photoPosts]
  );

  const submit = async (e) => {
    e?.preventDefault?.();
    let image_url = "";
    setUploading(true);
    try {
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        image_url = data.path;
      }
      if (!image_url && !caption.trim()) {
        toast.error(T("Add a caption or photo"));
        return;
      }
      await api.post("/feed", { caption, image_url });
      toast.success(T("Shared!"));
      setCaption("");
      setPhoto(null);
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setUploading(false); }
  };

  const addComment = async (id, text) => {
    if (!text.trim()) return;
    try { await api.post(`/feed/${id}/comment`, { text }); refresh(); } catch (e) { toast.error(formatErr(e)); }
  };

  const toggleLike = async (id) => {
    try { await api.post(`/feed/${id}/like`); refresh(); } catch (e) { toast.error(formatErr(e)); }
  };

  const openLightbox = (postId) => {
    const i = photoPosts.findIndex((p) => p.id === postId);
    if (i >= 0) setLbIndex(i);
  };

  return (
    <div className="space-y-3 fadein">
      <h2 className="font-display text-2xl text-motif">{T("Live feed")}</h2>
      <form onSubmit={submit} className="card-elev p-4 space-y-2">
        <input className="input-motif" placeholder={T("Say something…")} value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="feed-caption" />
        <PhotoPicker file={photo} onSelect={setPhoto} onBusy={setPhotoBusy} testIdPrefix="feed-photo" />
        <button type="submit" disabled={uploading || photoBusy} className="btn-primary w-full" data-testid="feed-submit">
          {photoBusy ? T("Preparing photo…") : uploading ? T("Sharing…") : T("Share")}
        </button>
      </form>
      {feed.map((p) => (
        <FeedPost key={p.id} post={p} addComment={addComment} toggleLike={toggleLike} onImageClick={() => openLightbox(p.id)} />
      ))}
      <Lightbox
        open={lbIndex >= 0}
        items={lbItems}
        index={lbIndex < 0 ? 0 : lbIndex}
        onClose={() => setLbIndex(-1)}
        onIndex={setLbIndex}
      />
    </div>
  );
}

function FeedPost({ post, addComment, toggleLike, onImageClick }) {
  const [c, setC] = useState("");
  const { user } = useAuth();
  const myId = user?.guest?.id || user?.user?.id;
  const likes = post.likes || [];
  const liked = myId ? likes.includes(myId) : false;
  return (
    <div className="card-elev overflow-hidden" data-testid={`feed-post-${post.id}`}>
      {post.image_url && (
        <button
          type="button"
          onClick={onImageClick}
          className="block w-full"
          data-testid={`feed-img-btn-${post.id}`}
        >
          <img src={fileUrl(post.image_url)} alt="" className="w-full h-72 object-cover" loading="lazy" />
        </button>
      )}
      <div className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-[10px] tracking-luxe text-motif-muted">{post.author_name}</div>
          <button
            type="button"
            onClick={() => toggleLike(post.id)}
            className="flex items-center gap-1 text-xs transition-transform active:scale-125"
            style={{ color: liked ? "#c2483f" : "var(--motif-text-muted)" }}
            aria-label={liked ? "Remove heart" : "Send a heart"}
            data-testid={`feed-like-${post.id}`}
          >
            <Heart className="w-4 h-4" fill={liked ? "#c2483f" : "none"} />
            {likes.length > 0 && <span className="tabular-nums" data-testid={`feed-like-count-${post.id}`}>{likes.length}</span>}
          </button>
        </div>
        {post.caption && <div className="text-sm text-motif">{post.caption}</div>}
        {(post.comments || []).slice(-3).map((cm) => (
          <div key={cm.id} className="text-xs text-motif"><span className="text-primary-motif">{cm.author}:</span> {cm.text}</div>
        ))}
        <form onSubmit={(e) => { e.preventDefault(); addComment(post.id, c); setC(""); }} className="flex gap-2 pt-1">
          <input className="input-motif text-sm py-2" placeholder="Comment…" value={c} onChange={(e) => setC(e.target.value)} />
          <button className="btn-ghost text-xs"><MessageCircle className="w-3.5 h-3.5" /></button>
        </form>
      </div>
    </div>
  );
}

function GamesTab() {
  const [view, setView] = useState("hub"); // hub | crossword | whowill
  const [crossword, setCrossword] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [session, setSession] = useState(null);
  const [introOpen, setIntroOpen] = useState(false);
  const [starting, setStarting] = useState(false);

  const refreshAttempt = async (cwId) => {
    if (!cwId) return;
    try {
      const { data } = await api.get(`/crossword/${cwId}/attempt`);
      setAttempt(data);
    } catch (err) { console.debug("crossword attempt fetch failed", err); }
  };

  useEffect(() => {
    api.get("/crossword/current").then(async ({ data }) => {
      setCrossword(data);
      if (data?.id) refreshAttempt(data.id);
    }).catch(() => {});
    api.get("/who-will/session").then(({ data }) => setSession(data)).catch(() => {});
    const iv = setInterval(() => {
      api.get("/who-will/session").then(({ data }) => setSession(data)).catch(() => {});
    }, 8000);
    return () => clearInterval(iv);
  }, []);

  if (view === "crossword") {
    return (
      <CoupleCrossword
        skipConfirm
        onBack={async () => {
          setView("hub");
          if (crossword?.id) await refreshAttempt(crossword.id);
        }}
      />
    );
  }
  if (view === "whowill") return <WhoWillGuest onBack={() => setView("hub")} />;

  // Derive crossword card state:
  //  - completed & valid -> "View Results"
  //  - started but not completed -> "Continue"
  //  - not started -> "Play"
  let crosswordCta = "Play";
  let crosswordState = crossword ? "Live" : "Not Started";
  let introMode = "welcome";
  if (attempt) {
    if (attempt.submitted_at || (attempt.completed_at && attempt.valid)) {
      crosswordCta = "View Results";
      crosswordState = "Completed";
      introMode = "results";
    } else if (attempt.started_at) {
      crosswordCta = "Continue";
      introMode = "continue";
    }
  }

  const openIntro = () => setIntroOpen(true);

  const handlePrimary = async () => {
    if (!crossword) return;
    // For "welcome" mode we must first create the official attempt (server-authoritative timer).
    if (introMode === "welcome") {
      setStarting(true);
      try {
        const { data } = await api.post(`/crossword/${crossword.id}/start`);
        setAttempt(data);
      } catch (e) {
        toast.error(formatErr(e));
        setStarting(false);
        return;
      }
      setStarting(false);
    }
    // For "continue" mode, we simply open the grid — the server timer is already running.
    // For "results" mode, we also open the crossword view which auto-shows the leaderboard/status.
    setIntroOpen(false);
    setView("crossword");
  };

  const whoWillState = session?.state || "NOT_STARTED";
  const whoWillLive = whoWillState !== "NOT_STARTED" && whoWillState !== "COMPLETE";

  return (
    <div className="space-y-3 fadein" data-testid="games-tab">
      <h2 className="font-display text-2xl text-motif">{T("Games")}</h2>

      <GameCard
        title={T("Couple Crossword")}
        instructions={T("Solve the couple's mini crossword. One official attempt — the timer starts when you press Begin.")}
        state={crosswordState}
        onPlay={openIntro}
        cta={crosswordCta}
        available={!!crossword}
        testid="game-card-crossword"
      />

      <GameCard
        title={T("Who Will?")}
        instructions={`${T("When the MC starts the game, vote live for")} ${session?.couple?.aimee || "AIMEE"} ${T("or")} ${session?.couple?.riyaz || "RIYAZ"}.`}
        state={whoWillState === "COMPLETE" ? "Completed" : whoWillLive ? "Live" : "Not Started"}
        onPlay={() => setView("whowill")}
        cta="Join"
        available={whoWillLive}
        testid="game-card-whowill"
      />

      {introOpen && crossword && (
        <CrosswordIntroModal
          mode={introMode}
          brideName={session?.couple?.aimee ? capitalize(session.couple.aimee) : "Aimee"}
          groomName={session?.couple?.riyaz ? capitalize(session.couple.riyaz) : "Riyaz"}
          prizeText={crossword.prize_text || "Top the leaderboard and win a prize."}
          onClose={() => setIntroOpen(false)}
          onPrimary={handlePrimary}
          primaryLoading={starting}
        />
      )}
    </div>
  );
}

function capitalize(s) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

// Simple embedded PDF map viewer was replaced in Phase 4C-2 by VenueMapModal
// (react-zoom-pan-pinch over the canonical LegacyMap PNG asset).

function GameCard({ title, instructions, state, onPlay, cta, available, children, testid }) {
  const badgeColor =
    state === "Live" ? "var(--motif-primary)" :
    state === "Completed" ? "#6b7280" : "#9ca3af";
  return (
    <div className="card-elev p-4" data-testid={testid}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg text-motif">{title}</div>
          <div className="text-[11px] text-motif-muted mt-0.5 leading-snug">{instructions}</div>
        </div>
        <span
          className="text-[9px] tracking-luxe px-2 py-1 rounded-full whitespace-nowrap"
          style={{ background: "var(--motif-accent)", color: badgeColor }}
          data-testid={`${testid}-state`}
        >
          {T(state)}
        </span>
      </div>
      {onPlay !== null && (
        <button
          onClick={onPlay}
          disabled={!available}
          className="btn-primary w-full text-sm disabled:opacity-50"
          data-testid={`${testid}-play`}
        >
          {T(cta)}
        </button>
      )}
      {children}
    </div>
  );
}

function HelpTab() {
  const [kind, setKind] = useState("server");
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photo, setPhoto] = useState(null);
  const submit = async () => {
    setUploading(true);
    let image_url = "";
    try {
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        image_url = data.path;
      }
      await api.post("/requests/help", { kind, notes, image_url });
      toast.success(T("Help is on the way"));
      setNotes("");
      setPhoto(null);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setUploading(false); }
  };
  const KINDS = [
    { id: "server", label: "Need a server" },
    { id: "spill", label: "Spill / cleanup" },
    { id: "broken_glass", label: "Broken glass" },
    { id: "lost_item", label: "Lost item" },
    { id: "maintenance", label: "Maintenance" },
    { id: "other", label: "Other" },
  ];
  return (
    <div className="space-y-3 fadein">
      <h2 className="font-display text-2xl text-motif">{T("Need help?")}</h2>
      <div className="space-y-2">
        {KINDS.map((k) => (
          <button
            key={k.id}
            onClick={() => setKind(k.id)}
            className="w-full text-left card-elev p-4"
            style={{ borderColor: kind === k.id ? "var(--motif-primary)" : "var(--motif-border)" }}
            data-testid={`help-${k.id}`}
          >
            <div className="font-display text-lg text-motif">{T(k.label)}</div>
          </button>
        ))}
      </div>
      <textarea className="input-motif" rows={3} placeholder={T("Anything we should know?")} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="help-notes" />
      <div className="card-elev p-3">
        <div className="text-[10px] tracking-luxe text-motif-muted mb-2">{T("attach a photo (optional)")}</div>
        <PhotoPicker file={photo} onSelect={setPhoto} onBusy={setPhotoBusy} testIdPrefix="help-photo" />
      </div>
      <button onClick={submit} disabled={uploading || photoBusy} className="btn-primary w-full" data-testid="help-submit">
        {photoBusy ? T("Preparing photo…") : uploading ? T("Sending…") : T("Send to coordinator")}
      </button>
    </div>
  );
}

// Phase 3B: Requests contains exactly Food, Photo Q, Embroidery, Help, Music.
function RequestsHub({ myReqs, refresh, menu, guest, music, refreshMusic, initialSub }) {
  const [sub, setSub] = useState(initialSub || "food");
  useEffect(() => {
    if (initialSub) setSub(initialSub);
  }, [initialSub]);
  const primary = [
    { id: "food", label: "Food", icon: Utensils },
    { id: "queue", label: "Photo Q", icon: Camera },
    { id: "embroidery", label: "Embroidery", icon: Scissors },
    { id: "music", label: "Music", icon: Music },
  ];
  const helpSub = { id: "help", label: "Help", icon: HelpCircle };
  const tabBtn = (s) => {
    const Icon = s.icon;
    const active = sub === s.id;
    return (
      <button
        key={s.id}
        onClick={() => setSub(s.id)}
        className="flex-1 min-w-[80px] px-3 py-2 rounded-full text-xs tracking-wide transition flex items-center justify-center gap-1.5"
        style={{
          background: active ? "var(--motif-primary)" : "var(--motif-accent)",
          color: active ? "var(--motif-primary-fg)" : "var(--motif-text-muted)",
        }}
        data-testid={`req-sub-${s.id}`}
      >
        {/* flex-shrink-0 + explicit min size: the SVG must never be flex-crushed
            to zero width on narrow mobile rows (Embroidery scissors bug) */}
        <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ minWidth: 14, minHeight: 14 }} strokeWidth={1.5} aria-hidden="true" />
        <span className="whitespace-nowrap">{T(s.label)}</span>
      </button>
    );
  };
  return (
    <div className="space-y-4 fadein">
      <div className="space-y-2">
        <div className="flex gap-1 overflow-x-auto no-scrollbar -mx-1 px-1">
          {primary.map(tabBtn)}
        </div>
        <div className="flex">
          {tabBtn(helpSub)}
        </div>
      </div>
      {sub === "food" && <FoodTab myReqs={myReqs} refresh={refresh} menu={menu} />}
      {sub === "queue" && <PhotoQueueCard />}
      {sub === "embroidery" && <EmbroideryFlow guest={guest} />}
      {sub === "help" && <HelpTab />}
      {sub === "music" && <MusicTab music={music} guestId={guest.id} refresh={refreshMusic} />}
    </div>
  );
}

function MoreHub({ setTab, schedule, guest, event, focusSignal, onFocusHandled, initialSub }) {
  const [sub, setSub] = useState(initialSub || "schedule");
  useEffect(() => { if (initialSub) setSub(initialSub); }, [initialSub]);
  const subs = [
    { id: "schedule", label: t("schedule"), icon: Calendar },
    { id: "table", label: t("table"), icon: MapPin },
    { id: "language", label: t("language"), icon: Languages },
    { id: "livestream", label: t("livestream"), icon: Video },
  ];
  return (
    <div className="space-y-4 fadein">
      <div className="grid grid-cols-2 gap-3">
        {subs.map((s) => {
          const Icon = s.icon;
          const active = sub === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setSub(s.id)}
              className="card-elev p-4 min-h-[72px] flex flex-col items-center justify-center gap-1.5 transition"
              style={{
                borderColor: active ? "var(--motif-primary)" : "var(--motif-border)",
                background: active ? "var(--motif-accent)" : "var(--motif-surface)",
              }}
              data-testid={`more-sub-${s.id}`}
            >
              <Icon className="w-4 h-4 text-primary-motif flex-shrink-0" strokeWidth={1.5} />
              <span className="text-[11px] text-motif text-center">{s.label}</span>
            </button>
          );
        })}
      </div>
      {sub === "schedule" && <ScheduleTab schedule={schedule} event={event} focusSignal={focusSignal} onFocusHandled={onFocusHandled} />}
      {sub === "table" && <TableTab guest={guest} />}
      {sub === "livestream" && <LivestreamPlayer />}
      {sub === "language" && <LanguagePicker />}
    </div>
  );
}

function LanguagePicker() {
  const [lang, setLangState] = useState(getLang());
  const choose = (id) => {
    setLang(id);
    setLangState(id);
    // Persist to the guest profile so the language follows them across devices.
    api.put("/guests/me/language", { language: id }).catch(() => {});
    toast.success(`Language: ${LANGS.find((l) => l.id === id)?.label}`);
  };
  return (
    <div className="space-y-3 fadein">
      <h2 className="font-display text-2xl text-motif">{t("language")}</h2>
      <p className="text-sm text-motif-muted">{T("UI labels translate; event content stays in the original.")}</p>
      <div className="space-y-2">
        {LANGS.map((l) => (
          <button
            key={l.id}
            onClick={() => choose(l.id)}
            className="w-full text-left card-elev p-4"
            style={{ borderColor: lang === l.id ? "var(--motif-primary)" : "var(--motif-border)" }}
            data-testid={`lang-${l.id}`}
          >
            <div className="font-display text-lg text-motif">{l.label}</div>
            {lang === l.id && <div className="text-[10px] tracking-luxe text-primary-motif mt-1">{T("selected")}</div>}
          </button>
        ))}
      </div>
    </div>
  );
}
